import { type FC, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { isNil } from 'lodash';
// Import Types
import type { PartnerLanguage } from 'types';
// Import UI Components
import { Option, PopoverV2, Tag } from '@geneui/components';
// Import Constants
import { l } from 'constants/common';
import { LanguagesFlagsPaths } from 'constants/utils';
// Import Styles
import styles from './customerJourneyVisualizationLanguages.module.scss';

interface Props {
    languages: string[];
    partnerLanguages: Record<string, PartnerLanguage>;
}

const CustomerJourneyVisualizationLanguages: FC<Props> = ({ languages, partnerLanguages }) => {
    const { t } = useTranslation();

    if (languages.length === 0) {
        return <Tag name={t(l.Default)} cornerRadius="smooth-radius" appearance="outline" />;
    }

    return (
        <PopoverV2
            className={styles.languagesPopover}
            position="right"
            align="center"
            behave="toggle"
            Content={
                <div className={styles.languagesPopoverList}>
                    {languages.map((language) => {
                        const name = partnerLanguages[language]?.Name ?? language;
                        const flagPath = LanguagesFlagsPaths[language];
                        return (
                            <Option
                                key={language}
                                title={name}
                                leftCustomElement={
                                    isNil(flagPath) ? null : (
                                        <img className={styles.languagesPopoverFlag} src={flagPath} alt={name} />
                                    )
                                }
                            />
                        );
                    })}
                </div>
            }
        >
            <Tag name={`${languages.length} ${t(l.Languages)}`} cornerRadius="smooth-radius" appearance="outline" />
        </PopoverV2>
    );
};

CustomerJourneyVisualizationLanguages.displayName = 'CustomerJourneyVisualizationLanguages';

export default memo(CustomerJourneyVisualizationLanguages);
